import { h } from './core.js';
import { renderIcon } from './icons.js';

/*
 * ContextMenu: a keyboard-accessible popup menu, opened at a point or anchored
 * to an element. Used by Diagram for node menus and available on its own.
 *
 *   const menu = new dfc.ContextMenu({ host: document.body });
 *   menu.show({ x, y, items: [
 *     { name: 'Rename', icon: 'pencil', shortcut: 'F2', action: (ctx) => ... },
 *     { name: 'Colour', swatches: ['#ef4444', '#3b82f6'], selected: '#3b82f6', action: (ctx, colour) => ... },
 *     'separator',
 *     { name: 'Delete', icon: 'trash', action: (ctx) => ..., cssClass: 'is-danger' },
 *   ], ctx: { node } });
 *
 * Items: { name, icon, shortcut, disabled, checked, children | subMenu, action(ctx), separator, cssClass,
 *          swatches: [colour], selected }  or the string 'separator', or a function (ctx) => item.
 * Arrows move, Home and End jump, typing a letter jumps to the next matching item, Right opens a
 * submenu and Left closes it, Enter and Space invoke, Escape closes and returns focus.
 */

export function resolveMenuItems(items, ctx) {
	const out = [];
	(items || []).forEach((entry) => {
		const r = typeof entry === 'function' ? entry(ctx) : entry;
		if (r === null || r === undefined || r === false) return;
		if (r === 'separator') { out.push({ separator: true }); return; }
		if (typeof r === 'string') { out.push({ name: r }); return; }
		const nested = r.subMenu || r.children;
		out.push(nested ? Object.assign({}, r, { subMenu: resolveMenuItems(nested, ctx) }) : r);
	});
	return out.filter((item, i, list) => !item.separator || (i > 0 && i < list.length - 1 && !list[i - 1].separator));
}

export class ContextMenu {
	constructor(opts = {}) {
		this.host = opts.host || document.body;
		this.ctx = opts.ctx || {};
		this.onClose = opts.onClose || null;
		this.parent = opts.parent || null;
		this.root = null; this.child = null; this.itemNodes = []; this.items = []; this.focused = -1; this.open = false; this._off = [];
	}

	show(opts) {
		this.hide();
		if (opts.ctx) this.ctx = Object.assign({}, this.ctx, opts.ctx);
		this.items = resolveMenuItems(opts.items, this.ctx);
		this.returnFocus = opts.returnFocus || null;
		this.root = h('div', { className: 'dfc dfc-menu', role: 'menu', tabindex: '-1' });
		this.itemNodes = [];
		this.items.forEach((item, index) => {
			if (item.separator) { this.root.appendChild(h('div', { className: 'dfc-menu-separator', role: 'separator' })); return; }
			const node = h('div', { className: 'dfc-menu-item' + (item.cssClass ? ' ' + item.cssClass : '') + (item.swatches ? ' has-swatches' : ''), role: item.checked === undefined ? 'menuitem' : 'menuitemcheckbox', tabindex: '-1', 'aria-disabled': item.disabled ? 'true' : null, 'aria-checked': item.checked === undefined ? null : String(item.checked === true), 'aria-haspopup': item.subMenu ? 'true' : null, dataset: { index: String(index) } });
			if (item.checked !== undefined) node.appendChild(h('span', { className: 'dfc-menu-check' }, item.checked ? renderIcon('check') : null));
			if (item.icon) node.appendChild(h('span', { className: 'dfc-menu-icon' }, renderIcon(item.icon)));
			node.appendChild(h('span', { className: 'dfc-menu-label', text: item.name || '' }));
			if (item.swatches) {
				const row = h('span', { className: 'dfc-menu-swatches', role: 'group', 'aria-label': item.name || 'Colour' });
				item.swatches.forEach((c) => {
					row.appendChild(h('button', { type: 'button', className: 'dfc-menu-swatch' + (item.selected && String(item.selected).toLowerCase() === String(c).toLowerCase() ? ' is-selected' : ''), title: c, 'aria-label': c, style: { background: c }, onMousedown: (e) => e.preventDefault(), onClick: (e) => { e.stopPropagation(); if (item.disabled) return; this.hideAll(); if (typeof item.action === 'function') item.action(this.ctx, c); } }));
				});
				node.appendChild(row);
			}
			if (item.shortcut) node.appendChild(h('span', { className: 'dfc-menu-shortcut', text: item.shortcut }));
			if (item.subMenu) node.appendChild(h('span', { className: 'dfc-menu-arrow', text: '›' }));
			const nodeIndex = this.itemNodes.length;
			node.addEventListener('click', (e) => { if (e.target.closest('.dfc-menu-swatch')) return; this.invoke(nodeIndex); });
			node.addEventListener('mouseenter', () => { this.focus(nodeIndex); if (item.subMenu) this.openSubMenu(nodeIndex); else this.closeChild(); });
			this.itemNodes.push(node);
			this.root.appendChild(node);
		});
		this.host.appendChild(this.root);
		this.place(opts);
		this.open = true;
		this._onKey = (e) => this.onKeyDown(e);
		this.root.addEventListener('keydown', this._onKey);
		this._outside = (e) => { if (this.root && !this.root.contains(e.target) && !(this.child && this.child.root && this.child.root.contains(e.target))) this.hideAll(); };
		document.addEventListener('mousedown', this._outside, true);
		this._onScroll = () => this.hideAll();
		window.addEventListener('resize', this._onScroll);
		this.root.focus({ preventScroll: true });
		this.focus(this.firstEnabled());
		return this.root;
	}

	/** Position inside the host, clipped to the window; flips rather than overflows. */
	place(opts) {
		const hostRect = this.host.getBoundingClientRect ? this.host.getBoundingClientRect() : { left: 0, top: 0, width: Infinity, height: Infinity };
		const size = this.root.getBoundingClientRect();
		let x = opts.x || 0, y = opts.y || 0;
		if (opts.anchor && opts.anchor.getBoundingClientRect) { const a = opts.anchor.getBoundingClientRect(); if (opts.placement === 'below') { x = a.left; y = a.bottom; } else { x = a.right; y = a.top; } }
		const bounds = { left: Math.max(0, hostRect.left || 0), top: Math.max(0, hostRect.top || 0), right: Math.min(window.innerWidth || Infinity, (hostRect.left || 0) + (hostRect.width || Infinity)), bottom: Math.min(window.innerHeight || Infinity, (hostRect.top || 0) + (hostRect.height || Infinity)) };
		if (x + size.width > bounds.right) x -= size.width;
		if (x < bounds.left) x = bounds.left;
		if (y + size.height > bounds.bottom) y -= size.height;
		if (y < bounds.top) y = bounds.top;
		const fixed = this.host === document.body;
		this.root.style.position = fixed ? 'fixed' : 'absolute';
		this.root.style.left = Math.round(fixed ? x : x - hostRect.left + (this.host.scrollLeft || 0)) + 'px';
		this.root.style.top = Math.round(fixed ? y : y - hostRect.top + (this.host.scrollTop || 0)) + 'px';
	}

	firstEnabled() { for (let i = 0; i < this.itemNodes.length; i++) if (!this.itemAt(i).disabled) return i; return -1; }
	itemAt(nodeIndex) { const node = this.itemNodes[nodeIndex]; return node ? this.items[Number(node.dataset.index)] : null; }
	focus(nodeIndex) {
		if (nodeIndex < 0 || nodeIndex >= this.itemNodes.length) return;
		const prev = this.itemNodes[this.focused]; if (prev) prev.classList.remove('is-focused');
		this.focused = nodeIndex;
		const node = this.itemNodes[nodeIndex]; node.classList.add('is-focused'); node.focus({ preventScroll: true });
	}
	step(delta) {
		const n = this.itemNodes.length; if (!n) return;
		let i = this.focused;
		for (let k = 0; k < n; k++) { i = (i + delta + n) % n; if (!this.itemAt(i).disabled) { this.focus(i); return; } }
	}
	invoke(nodeIndex) {
		const item = this.itemAt(nodeIndex); if (!item || item.disabled) return;
		if (item.subMenu) { this.openSubMenu(nodeIndex); return; }
		if (item.swatches) return;
		this.hideAll();
		if (typeof item.action === 'function') item.action(this.ctx);
	}
	openSubMenu(nodeIndex) {
		const item = this.itemAt(nodeIndex); if (!item || !item.subMenu) return;
		if (this.child && this.child.anchorIndex === nodeIndex && this.child.open) return;
		this.closeChild();
		this.child = new ContextMenu({ host: this.host, ctx: this.ctx, parent: this });
		this.child.anchorIndex = nodeIndex;
		this.itemNodes[nodeIndex].setAttribute('aria-expanded', 'true');
		this.child.show({ items: item.subMenu, anchor: this.itemNodes[nodeIndex], placement: 'right' });
	}
	closeChild() { if (this.child) { const i = this.child.anchorIndex; if (this.itemNodes[i]) this.itemNodes[i].setAttribute('aria-expanded', 'false'); this.child.hide(); this.child = null; } }
	onKeyDown(e) {
		const k = e.key;
		if (k === 'ArrowDown') { e.preventDefault(); this.step(1); }
		else if (k === 'ArrowUp') { e.preventDefault(); this.step(-1); }
		else if (k === 'Home') { e.preventDefault(); this.focus(this.firstEnabled()); }
		else if (k === 'End') { e.preventDefault(); for (let i = this.itemNodes.length - 1; i >= 0; i--) if (!this.itemAt(i).disabled) { this.focus(i); break; } }
		else if (k === 'ArrowRight') { const it = this.itemAt(this.focused); if (it && it.subMenu) { e.preventDefault(); this.openSubMenu(this.focused); this.child.focus(this.child.firstEnabled()); } }
		else if (k === 'ArrowLeft') { if (this.parent) { e.preventDefault(); this.parent.closeChild(); this.parent.root.focus({ preventScroll: true }); this.parent.focus(this.parent.focused); } }
		else if (k === 'Enter' || k === ' ') { e.preventDefault(); const it = this.itemAt(this.focused); if (it && it.swatches) { const first = this.itemNodes[this.focused].querySelector('.dfc-menu-swatch.is-selected, .dfc-menu-swatch'); if (first) first.click(); } else this.invoke(this.focused); }
		else if (k === 'Escape') { e.preventDefault(); e.stopPropagation(); if (this.parent) { this.parent.closeChild(); this.parent.root.focus({ preventScroll: true }); } else this.hideAll(); }
		else if (k.length === 1 && /\S/.test(k)) {
			const n = this.itemNodes.length; const start = this.focused;
			for (let s = 1; s <= n; s++) { const i = (start + s) % n; const it = this.itemAt(i); if (it && !it.disabled && String(it.name || '').toLowerCase().startsWith(k.toLowerCase())) { this.focus(i); break; } }
		}
	}
	/** Closes this menu and its submenus. */
	hide() {
		this.closeChild();
		if (!this.root) return;
		this.root.removeEventListener('keydown', this._onKey);
		document.removeEventListener('mousedown', this._outside, true);
		window.removeEventListener('resize', this._onScroll);
		this.root.remove(); this.root = null; this.itemNodes = []; this.focused = -1;
		const wasOpen = this.open; this.open = false;
		if (wasOpen && this.returnFocus && this.returnFocus.focus) { try { this.returnFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
		if (wasOpen && typeof this.onClose === 'function') this.onClose();
	}
	/** Closes the whole menu tree from any level. */
	hideAll() { let m = this; while (m.parent) m = m.parent; m.hide(); }
	destroy() { this.hide(); }
}

